/**
 * #627 Live Activity: one current, meaningful line per active actor, projected from canonical
 * Brunel state (public.live_activity_facts). Pure.
 *
 * This is presentation, not a log and not authority:
 *   - nothing is appended; every call re-derives the CURRENT line for each actor, so an actor
 *     whose state changes (coding -> testing -> review -> merging) keeps the same actorKey and
 *     its line is replaced, never duplicated;
 *   - no terminal line, run tail, result, chat or command prose is used - titles and states only;
 *   - an actor whose state cannot be derived is shown as UNKNOWN, never guessed;
 *   - a line is never evidence or permission for anything: the canonical rows it cites are.
 */


export const LIVE_ACTIVITY_STATES = [
  'WORKING', 'TESTING', 'REVIEWING', 'MERGING', 'WAITING', 'BLOCKED', 'HEALTH', 'DONE', 'UNKNOWN',
] as const;
export type LiveActivityState = (typeof LIVE_ACTIVITY_STATES)[number];

export type LiveActivityActorKind = 'worker' | 'overseer' | 'machinist' | 'node_update';

// ---------------------------------------------------------------------------
// Canonical facts (the RPC's value)
// ---------------------------------------------------------------------------

export interface LiveActivityWorkerFacts {
  readonly workerId: string;
  readonly number: number;
  readonly name: string;
  readonly status: string | null;
  readonly nodeId: string | null;
  readonly active: {
    readonly assignmentId: string;
    readonly runId: string | null;
    readonly taskId: string | null;
    readonly taskTitle: string | null;
    readonly taskStatus: string | null;
    readonly startedAt: string | null;
  } | null;
  readonly completion: {
    readonly assignmentId: string | null;
    readonly stage: string;
    readonly outcome: string;
    readonly recordedAt: string;
    readonly taskTitle: string | null;
    readonly pullRequest: { readonly repository: string; readonly number: number } | null;
  } | null;
  readonly chat: { readonly origin: string | null; readonly claimedAt: string | null } | null;
}

export interface LiveActivityMachineFacts {
  readonly nodeId: string;
  readonly name: string;
  readonly status: string | null;
  readonly lastSeenAt: string | null;
  readonly workerSeats: number | null;
  readonly activeRuns: number | null;
  readonly accepting: boolean | null;
  readonly holdingBack: string | null;
  readonly runningVersion: string | null;
  readonly targetVersion: string | null;
  readonly updateState: string | null;
  readonly updateVersion: string | null;
  readonly updateReportedAt: string | null;
  readonly behind: boolean | null;
  readonly outages: readonly {
    readonly runtimeKey: string;
    readonly runtimeLabel: string | null;
    readonly reasonCode: string;
    readonly observedAt: string | null;
    readonly expired: boolean;
  }[];
  readonly rescan: { readonly issuedAt: string; readonly acknowledgedAt: string | null; readonly outcome: string | null } | null;
}

export interface LiveActivityFacts {
  readonly readAt: string;
  /** Null: that section could not be read; its actors are UNKNOWN, never guessed. */
  readonly workers: readonly LiveActivityWorkerFacts[] | null;
  readonly plannerClaims: readonly { readonly commandSeq: number; readonly commandKind: string | null; readonly ownerKind: string | null; readonly claimedAt: string | null }[] | null;
  readonly discussionTurns: readonly { readonly role: 'overseer' | 'ux' | 'core'; readonly discussionId: string; readonly threadId: string; readonly threadTitle: string | null; readonly round: number | null; readonly claimedAt: string | null; readonly caseNumber: number | null }[] | null;
  readonly observerReplyClaims: readonly { readonly role: 'ux' | 'core'; readonly threadId: string | null; readonly threadTitle: string | null; readonly claimedAt: string | null }[] | null;
  /** Reviews a computer holds right now. The one Reviewer's are role `reviewer`; `ux` / `core` are the two old reviewers' and are no longer taken. */
  readonly observerReviewClaims: readonly { readonly role: 'ux' | 'core' | 'reviewer'; readonly lane: string | null; readonly claimedAt: string | null }[] | null;
  readonly discussions: readonly { readonly discussionId: string; readonly threadId: string; readonly threadTitle: string | null; readonly round: number | null; readonly maxRounds: number | null; readonly nextRoles: readonly string[]; readonly updatedAt: string | null; readonly caseNumber: number | null }[] | null;
  readonly machines: readonly LiveActivityMachineFacts[] | null;
}

// ---------------------------------------------------------------------------
// Projection
// ---------------------------------------------------------------------------

/** Canonical records a line points at. Present only when the facts carry them. */
export interface LiveActivityRefs {
  readonly workerId?: string;
  readonly taskId?: string;
  readonly runId?: string;
  readonly assignmentId?: string;
  readonly pullRequest?: { readonly repository: string; readonly number: number };
  readonly threadId?: string;
  readonly discussionId?: string;
  readonly commandSeq?: number;
  readonly nodeId?: string;
  /** The #627 Case this activity is about ("#B-1042"), when canonical state links one. */
  readonly caseReference?: string;
}

export interface LiveActivityRow {
  /** Stable per actor: the same actor's line is replaced as its state advances. */
  readonly actorKey: string;
  readonly actorKind: LiveActivityActorKind;
  readonly actorLabel: string;
  readonly state: LiveActivityState;
  /** Short, owner-readable, no prose from logs/results/chat. */
  readonly action: string;
  /** When the current state began, as the canonical row says; null when Unknown. */
  readonly since: string | null;
  /** Which canonical store the line was derived from (provenance, not authority). */
  readonly source: string;
  readonly refs: LiveActivityRefs;
}

export const LIVE_ACTIVITY_LIMITS = { workers: 16, machines: 10, actionChars: 160 } as const;
const PRESENCE_WINDOW_MS = 60_000;

const clip = (text: string, max: number = LIVE_ACTIVITY_LIMITS.actionChars): string => {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1)}…`;
};
const words = (code: string) => code.replaceAll('_', ' ');
const titled = (title: string | null, fallback = 'its task') => (title === null ? fallback : `"${clip(title, 90)}"`);
const prText = (pr: { readonly number: number } | null) => (pr === null ? '' : ` (PR #${pr.number})`);

/** "#B-1042" for Case number 1042 (cases were removed; older discussion facts may still name one). */
const caseReference = (number: number): string => `#B-${number}`;

/** A discussion's subject: its #627 Case token first when one is linked, then the thread title. */
type DiscussionSubject = { readonly discussionId: string; readonly threadId: string; readonly threadTitle: string | null; readonly caseNumber: number | null };
const subject = (d: DiscussionSubject) =>
  d.caseNumber === null ? titled(d.threadTitle, 'a finding') : `${caseReference(d.caseNumber)} ${titled(d.threadTitle, '')}`.trim();
const discussionRefs = (d: DiscussionSubject): LiveActivityRefs => ({
  discussionId: d.discussionId, threadId: d.threadId, ...(d.caseNumber === null ? {} : { caseReference: caseReference(d.caseNumber) }),
});

function worker(w: LiveActivityWorkerFacts, machines: ReadonlyMap<string, LiveActivityMachineFacts>, readAt: number): LiveActivityRow {
  const base = { actorKey: `worker:${w.workerId}`, actorKind: 'worker' as const, actorLabel: `Worker ${w.number}` };
  const refs: LiveActivityRefs = { workerId: w.workerId, ...(w.nodeId === null ? {} : { nodeId: w.nodeId }) };
  const machine = w.nodeId === null ? undefined : machines.get(w.nodeId);
  const inTouch = machine === undefined ? null : online(machine, readAt);

  if (w.active !== null) {
    const a = w.active;
    const on = { ...refs, assignmentId: a.assignmentId, ...(a.taskId === null ? {} : { taskId: a.taskId }), ...(a.runId === null ? {} : { runId: a.runId }) };
    const row = (state: LiveActivityState, action: string): LiveActivityRow =>
      ({ ...base, state, action: clip(action), since: a.startedAt, source: 'assignments/tasks', refs: on });
    if (inTouch === false) return row('BLOCKED', `${titled(a.taskTitle)} is on ${machine!.name}, which is out of touch`);
    switch (a.taskStatus) {
      case 'verifying': return row('TESTING', `verifying ${titled(a.taskTitle)}`);
      case 'blocked': return row('BLOCKED', `blocked on ${titled(a.taskTitle)}`);
      case 'awaiting_approval': return row('WAITING', `waiting for owner approval on ${titled(a.taskTitle)}`);
      case 'waiting': return row('WAITING', `waiting to start ${titled(a.taskTitle)}`);
      case 'working': return row('WORKING', `working on ${titled(a.taskTitle)}`);
      default: return row('UNKNOWN', `assigned ${titled(a.taskTitle)}; task state ${a.taskStatus ?? 'Unknown'}`);
    }
  }

  if (w.chat !== null) {
    const answering = w.chat.origin === 'supervisor' ? 'answering a Supervisor question' : 'answering the owner in Main Command';
    // A lease on a machine that is out of touch is not work in progress: say so, consistently.
    return inTouch === false
      ? { ...base, state: 'BLOCKED', action: clip(`${answering}, but ${machine!.name} is out of touch`), since: w.chat.claimedAt, source: 'worker_chat_turns', refs }
      : { ...base, state: 'WORKING', action: answering, since: w.chat.claimedAt, source: 'worker_chat_turns', refs };
  }

  if (w.completion !== null) {
    const c = w.completion;
    const on = { ...refs, ...(c.assignmentId === null ? {} : { assignmentId: c.assignmentId }), ...(c.pullRequest === null ? {} : { pullRequest: c.pullRequest }) };
    const what = `${titled(c.taskTitle, 'its work')}${prText(c.pullRequest)}`;
    const row = (state: LiveActivityState, action: string): LiveActivityRow =>
      ({ ...base, state, action: clip(action), since: c.recordedAt, source: `worker_completion_actions:${c.stage}`, refs: on });
    const stage = completionState(c.stage, c.outcome);
    return row(stage.state, `${stage.verb} ${what}`);
  }

  if (inTouch === false) return { ...base, state: 'BLOCKED', action: clip(`idle; ${machine!.name} is out of touch`), since: machine!.lastSeenAt, source: 'node_presence', refs };
  if (w.status === 'offline') return { ...base, state: 'BLOCKED', action: 'offline', since: null, source: 'workers', refs };
  return { ...base, state: 'WAITING', action: 'idle, no assigned task', since: null, source: 'workers', refs };
}

/**
 * The completion loop's stored stage/outcome as the Worker's current state (#597). The
 * Overseer decision outcomes are the stored forms of phaseOf(): merge / advance / fix_required
 * / blocked / waiting / owner_gate.
 */
export function completionState(stage: string, outcome: string): { readonly state: LiveActivityState; readonly verb: string } {
  if (stage === 'supervisor_report') return { state: 'REVIEWING', verb: 'completion under review:' };
  if (stage === 'overseer_decision') {
    switch (outcome) {
      case 'merge': return { state: 'MERGING', verb: 'merge approved for' };
      case 'advance': return { state: 'DONE', verb: 'finished' };
      case 'fix_required': return { state: 'WAITING', verb: 'fix required on' };
      case 'blocked': return { state: 'BLOCKED', verb: 'completion blocked:' };
      case 'waiting': return { state: 'WAITING', verb: 'completion waiting:' };
      case 'owner_gate': return { state: 'WAITING', verb: 'owner decision needed on' };
      default: return { state: 'UNKNOWN', verb: `Overseer decided ${words(outcome)} on` };
    }
  }
  if (stage === 'merge') {
    return outcome === 'merged' ? { state: 'DONE', verb: 'merged' } : { state: 'BLOCKED', verb: `merge ${words(outcome)}:` };
  }
  if (stage === 'integration') {
    return outcome === 'merged_externally' ? { state: 'DONE', verb: 'merged outside the loop:' } : { state: 'UNKNOWN', verb: `integration ${words(outcome)}:` };
  }
  if (stage === 'next_task') {
    // #597 repair loop: the Overseer asked the same Worker to repair its own work.
    if (outcome === 'repair_requested') return { state: 'WORKING', verb: 'repair requested for' };
    if (outcome === 'repair_exhausted') return { state: 'BLOCKED', verb: 'repair attempts exhausted for' };
    if (outcome === 'repair_blocked') return { state: 'BLOCKED', verb: 'repair blocked for' };
    return outcome === 'denied' ? { state: 'WAITING', verb: 'no next task yet after' } : { state: 'DONE', verb: 'next task assigned after' };
  }
  return { state: 'UNKNOWN', verb: `${words(stage)} ${words(outcome)}:` };
}

function online(machine: LiveActivityMachineFacts, readAt: number): boolean {
  if (machine.status === 'offline' || machine.lastSeenAt === null) return false;
  const seen = Date.parse(machine.lastSeenAt);
  return !Number.isNaN(seen) && readAt - seen <= PRESENCE_WINDOW_MS;
}

function overseer(facts: LiveActivityFacts): LiveActivityRow {
  const base = { actorKey: 'overseer', actorKind: 'overseer' as const, actorLabel: 'Overseer' };
  if (facts.discussionTurns === null || facts.plannerClaims === null || facts.workers === null || facts.discussions === null) {
    return { ...base, state: 'UNKNOWN', action: 'current work could not be read', since: null, source: 'live_activity_facts', refs: {} };
  }
  const turn = facts.discussionTurns.find((item) => item.role === 'overseer');
  if (turn !== undefined) {
    return { ...base, state: 'REVIEWING', action: clip(`moderating the discussion on ${subject(turn)}`),
      since: turn.claimedAt, source: 'observer_discussion_turns', refs: discussionRefs(turn) };
  }
  const plan = facts.plannerClaims[0];
  if (plan !== undefined) {
    return { ...base, state: 'WORKING', action: `${plan.commandKind === 'question' ? 'answering' : 'planning'} owner command #${plan.commandSeq}`,
      since: plan.claimedAt, source: 'command_center_planner_claims', refs: { commandSeq: plan.commandSeq } };
  }
  // A Supervisor-reviewed completion awaiting the Overseer's decision, then an approved merge.
  const reviewing = facts.workers.find((w) => w.active === null && w.completion?.stage === 'supervisor_report');
  if (reviewing !== undefined) {
    return { ...base, state: 'REVIEWING', action: clip(`reviewing Worker ${reviewing.number} completion${prText(reviewing.completion!.pullRequest)}`),
      since: reviewing.completion!.recordedAt, source: 'worker_completion_actions:supervisor_report',
      refs: { workerId: reviewing.workerId, ...(reviewing.completion!.pullRequest === null ? {} : { pullRequest: reviewing.completion!.pullRequest }) } };
  }
  const merging = facts.workers.find((w) => w.active === null && w.completion?.stage === 'overseer_decision' && w.completion.outcome === 'merge');
  if (merging !== undefined) {
    return { ...base, state: 'MERGING', action: clip(`merging Worker ${merging.number}${prText(merging.completion!.pullRequest)}`),
      since: merging.completion!.recordedAt, source: 'worker_completion_actions:overseer_decision',
      refs: { workerId: merging.workerId, ...(merging.completion!.pullRequest === null ? {} : { pullRequest: merging.completion!.pullRequest }) } };
  }
  const waiting = facts.discussions.find((d) => d.nextRoles.includes('overseer'));
  if (waiting !== undefined) {
    return { ...base, state: 'WAITING', action: clip(`due to moderate the discussion on ${subject(waiting)}`),
      since: waiting.updatedAt, source: 'observer_discussions', refs: discussionRefs(waiting) };
  }
  return { ...base, state: 'WAITING', action: 'no review or plan in progress', since: null, source: 'live_activity_facts', refs: {} };
}

function machinist(machine: LiveActivityMachineFacts, readAt: number): LiveActivityRow {
  const base = { actorKey: `machinist:${machine.nodeId}`, actorKind: 'machinist' as const, actorLabel: 'Machinist', refs: { nodeId: machine.nodeId } };
  const row = (state: LiveActivityState, action: string, since: string | null, source: string): LiveActivityRow =>
    ({ ...base, state, action: clip(action), since, source });
  if (!online(machine, readAt)) {
    return row('BLOCKED', `${machine.name} out of touch${machine.lastSeenAt === null ? '' : ` since ${machine.lastSeenAt}`}`, machine.lastSeenAt, 'node_presence');
  }
  if (machine.updateState === 'failed' || machine.updateState === 'rolled_back') {
    return row('BLOCKED', `${machine.name} update ${machine.updateVersion === null ? '' : `to ${machine.updateVersion} `}${words(machine.updateState)}`,
      machine.updateReportedAt, 'node_versions');
  }
  if (machine.rescan !== null && machine.rescan.acknowledgedAt === null) {
    return row('HEALTH', `rescanning runtimes on ${machine.name}`, machine.rescan.issuedAt, 'node_instructions:rescan_runtimes');
  }
  const outage = machine.outages.find((item) => !item.expired);
  if (outage !== undefined) {
    return row('HEALTH', `${machine.name}: ${outage.runtimeLabel ?? outage.runtimeKey} unavailable (${words(outage.reasonCode)})`, outage.observedAt, 'runtime_outages');
  }
  const seats = machine.workerSeats === null || machine.activeRuns === null ? '' : ` · ${machine.activeRuns}/${machine.workerSeats} active seats`;
  if (machine.accepting === false) {
    return row('HEALTH', `${machine.name} holding new work back${machine.holdingBack === null ? '' : ` (${words(machine.holdingBack)})`}${seats}`, machine.lastSeenAt, 'node_capacity');
  }
  if (machine.accepting === null) return row('UNKNOWN', `${machine.name} in touch; capacity not observed`, machine.lastSeenAt, 'node_presence');
  return row('HEALTH', `${machine.name} healthy${seats}`, machine.lastSeenAt, 'node_capacity');
}

/** A node update in flight or stuck; nothing when the node is on its target (no line spam). */
function nodeUpdate(machine: LiveActivityMachineFacts, readAt: number): LiveActivityRow | null {
  if (machine.targetVersion === null || machine.behind !== true) return null;
  const base = { actorKey: `node_update:${machine.nodeId}`, actorKind: 'node_update' as const, actorLabel: 'Node update', refs: { nodeId: machine.nodeId } };
  const failed = (machine.updateState === 'failed' || machine.updateState === 'rolled_back') && machine.updateVersion === machine.targetVersion;
  if (failed) {
    return { ...base, state: 'BLOCKED', action: clip(`${machine.name} update to ${machine.targetVersion} ${words(machine.updateState!)}`), since: machine.updateReportedAt, source: 'node_versions' };
  }
  return {
    ...base,
    state: online(machine, readAt) ? 'WORKING' : 'WAITING',
    action: clip(`${machine.name} moving from ${machine.runningVersion ?? 'Unknown'} to ${machine.targetVersion}${online(machine, readAt) ? '' : ' (machine out of touch)'}`),
    since: machine.updateReportedAt, source: 'node_versions',
  };
}

/**
 * One current line per actor, in a fixed order: Workers by number, Overseer, then
 * per machine its Machinist health line and any node update in flight.
 */
export function projectLiveActivity(facts: LiveActivityFacts): readonly LiveActivityRow[] {
  const readAt = Date.parse(facts.readAt);
  const machines = new Map((facts.machines ?? []).map((machine) => [machine.nodeId, machine]));
  const rows: LiveActivityRow[] = [];
  if (facts.workers === null) {
    rows.push({ actorKey: 'workers', actorKind: 'worker', actorLabel: 'Workers', state: 'UNKNOWN', action: 'Worker state could not be read', since: null, source: 'live_activity_facts', refs: {} });
  } else {
    for (const w of [...facts.workers].sort((a, b) => a.number - b.number).slice(0, LIVE_ACTIVITY_LIMITS.workers)) {
      rows.push(worker(w, machines, readAt));
    }
  }
  rows.push(overseer(facts));
  if (facts.machines === null) {
    rows.push({ actorKey: 'machinist', actorKind: 'machinist', actorLabel: 'Machinist', state: 'UNKNOWN', action: 'machine health could not be read', since: null, source: 'live_activity_facts', refs: {} });
  } else {
    for (const machine of [...facts.machines].sort((a, b) => a.name.localeCompare(b.name)).slice(0, LIVE_ACTIVITY_LIMITS.machines)) {
      rows.push(machinist(machine, readAt));
      const update = nodeUpdate(machine, readAt);
      if (update !== null) rows.push(update);
    }
  }
  return rows;
}

/** "Worker 1 · WORKING · working on "Role playbooks"" - the compact text form. */
export function formatLiveActivityLine(row: LiveActivityRow): string {
  return `${row.actorLabel} · ${row.state} · ${row.action}`;
}

// ---------------------------------------------------------------------------
// Parsing (defensive: a malformed section is Unknown, never guessed)
// ---------------------------------------------------------------------------

type Json = Record<string, unknown>;
const rec = (value: unknown): Json | null => (typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Json : null);
const s = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);
const n = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const b = (value: unknown): boolean | null => (typeof value === 'boolean' ? value : null);

function list<T>(value: unknown, parse: (row: Json) => T | null): readonly T[] | null {
  if (!Array.isArray(value)) return null;
  const out: T[] = [];
  for (const item of value) {
    const row = rec(item);
    const parsed = row === null ? null : parse(row);
    if (parsed === null) return null;
    out.push(parsed);
  }
  return out;
}

const role3 = (value: unknown) => (value === 'overseer' || value === 'ux' || value === 'core' ? value : null);
const role2 = (value: unknown) => (value === 'ux' || value === 'core' ? value : null);
const reviewRole = (value: unknown) => (value === 'ux' || value === 'core' || value === 'reviewer' ? value : null);

export function parseLiveActivityFacts(value: unknown): LiveActivityFacts | null {
  const root = rec(value);
  const readAt = s(root?.['readAt']);
  if (root === null || readAt === null || Number.isNaN(Date.parse(readAt))) return null;
  return {
    readAt,
    workers: list(root['workers'], (row) => {
      const workerId = s(row['workerId']);
      const number = n(row['number']);
      if (workerId === null || number === null) return null;
      const active = rec(row['active']);
      const completion = rec(row['completion']);
      const chat = rec(row['chat']);
      const pr = rec(completion?.['pullRequest']);
      const assignmentId = s(active?.['assignmentId']);
      const stage = s(completion?.['stage']);
      const outcome = s(completion?.['outcome']);
      const recordedAt = s(completion?.['recordedAt']);
      return {
        workerId, number, name: s(row['name']) ?? `Worker ${number}`, status: s(row['status']), nodeId: s(row['nodeId']),
        active: active === null || assignmentId === null ? null : {
          assignmentId, runId: s(active['runId']), taskId: s(active['taskId']), taskTitle: s(active['taskTitle']),
          taskStatus: s(active['taskStatus']), startedAt: s(active['startedAt']),
        },
        completion: completion === null || stage === null || outcome === null || recordedAt === null ? null : {
          assignmentId: s(completion['assignmentId']), stage, outcome, recordedAt, taskTitle: s(completion['taskTitle']),
          pullRequest: pr === null || s(pr['repository']) === null || n(pr['number']) === null
            ? null : { repository: s(pr['repository'])!, number: n(pr['number'])! },
        },
        chat: chat === null ? null : { origin: s(chat['origin']), claimedAt: s(chat['claimedAt']) },
      };
    }),
    plannerClaims: list(root['plannerClaims'], (row) => {
      const commandSeq = n(row['commandSeq']);
      return commandSeq === null ? null : { commandSeq, commandKind: s(row['commandKind']), ownerKind: s(row['ownerKind']), claimedAt: s(row['claimedAt']) };
    }),
    discussionTurns: list(root['discussionTurns'], (row) => {
      const role = role3(row['role']);
      const discussionId = s(row['discussionId']);
      const threadId = s(row['threadId']);
      return role === null || discussionId === null || threadId === null ? null
        : { role, discussionId, threadId, threadTitle: s(row['threadTitle']), round: n(row['round']), claimedAt: s(row['claimedAt']), caseNumber: n(row['caseNumber']) };
    }),
    observerReplyClaims: list(root['observerReplyClaims'], (row) => {
      const role = role2(row['role']);
      return role === null ? null : { role, threadId: s(row['threadId']), threadTitle: s(row['threadTitle']), claimedAt: s(row['claimedAt']) };
    }),
    observerReviewClaims: list(root['observerReviewClaims'], (row) => {
      const role = reviewRole(row['role']);
      return role === null ? null : { role, lane: s(row['lane']), claimedAt: s(row['claimedAt']) };
    }),
    discussions: list(root['discussions'], (row) => {
      const discussionId = s(row['discussionId']);
      const threadId = s(row['threadId']);
      const nextRoles = Array.isArray(row['nextRoles']) ? row['nextRoles'].filter((item): item is string => typeof item === 'string') : null;
      return discussionId === null || threadId === null || nextRoles === null ? null
        : { discussionId, threadId, threadTitle: s(row['threadTitle']), round: n(row['round']), maxRounds: n(row['maxRounds']), nextRoles, updatedAt: s(row['updatedAt']), caseNumber: n(row['caseNumber']) };
    }),
    machines: list(root['machines'], (row) => {
      const nodeId = s(row['nodeId']);
      const name = s(row['name']);
      const outages = list(row['outages'], (o) => {
        const runtimeKey = s(o['runtimeKey']);
        const reasonCode = s(o['reasonCode']);
        return runtimeKey === null || reasonCode === null ? null
          : { runtimeKey, runtimeLabel: s(o['runtimeLabel']), reasonCode, observedAt: s(o['observedAt']), expired: o['expired'] === true };
      });
      const rescan = rec(row['rescan']);
      if (nodeId === null || name === null || outages === null) return null;
      return {
        nodeId, name, status: s(row['status']), lastSeenAt: s(row['lastSeenAt']),
        workerSeats: n(row['workerSeats']), activeRuns: n(row['activeRuns']), accepting: b(row['accepting']), holdingBack: s(row['holdingBack']),
        runningVersion: s(row['runningVersion']), targetVersion: s(row['targetVersion']), updateState: s(row['updateState']),
        updateVersion: s(row['updateVersion']), updateReportedAt: s(row['updateReportedAt']), behind: b(row['behind']),
        outages,
        rescan: rescan === null || s(rescan['issuedAt']) === null ? null
          : { issuedAt: s(rescan['issuedAt'])!, acknowledgedAt: s(rescan['acknowledgedAt']), outcome: s(rescan['outcome']) },
      };
    }),
  };
}
